/**
 * EllaComplianceDashboard.tsx
 * Shows detected triggers, their recommended filings, and status.
 */

import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Loader2, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

interface ComplianceTrigger {
  id: string;
  company_id: string;
  trigger_type: string;
  reference_id: string | null;
  reason: string | null;
  filing_required: boolean;
  created_at: string;
  processed: boolean;
  processed_at: string | null;
}

export default function EllaComplianceDashboard({ companyId }: { companyId: string }) {
  const [rows, setRows] = useState<ComplianceTrigger[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  async function load() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('compliance_triggers')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      console.error('Error loading compliance triggers:', err);
      toast.error('Failed to load compliance triggers');
    } finally {
      setLoading(false);
    }
  }

  async function runNow() {
    try {
      setProcessing(true);
      toast.info('Running Ella compliance agent...');

      const { data, error } = await supabase.functions.invoke('ella-compliance-agent');

      if (error) {
        console.error('Error running Ella:', error);
        toast.error('Failed to run compliance agent');
      } else {
        toast.success(`Processed ${data?.processed || 0} triggers`);
        await load(); // Reload the triggers
      }
    } catch (err) {
      console.error('Error invoking Ella:', err);
      toast.error('Failed to invoke compliance agent');
    } finally {
      setProcessing(false);
    }
  }

  useEffect(() => {
    if (companyId) {
      load();

      // Subscribe to real-time updates
      const channel = supabase
        .channel('compliance_triggers')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'compliance_triggers',
            filter: `company_id=eq.${companyId}`,
          },
          () => {
            load();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [companyId]);

  const getTriggerBadge = (type: string) => {
    switch (type) {
      case 'DIRECTOR_CHANGE':
        return <Badge variant="default">Director Change</Badge>;
      case 'SHARE_ALLOTMENT':
        return <Badge variant="default">Share Allotment</Badge>;
      case 'MOI_AMENDMENT':
        return <Badge variant="default">MOI Amendment</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  const getStatusBadge = (processed: boolean) => {
    return processed ? (
      <Badge variant="default" className="bg-green-500 hover:bg-green-600">
        Processed
      </Badge>
    ) : (
      <Badge variant="secondary">Pending</Badge>
    );
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg font-semibold">Smart Compliance (Ella)</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Automatically detected statutory events requiring filings
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={runNow} disabled={processing || loading}>
          {processing ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Zap className="h-4 w-4 mr-1" />
          )}
          Run Now
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center p-8 text-muted-foreground">
            No compliance triggers detected yet
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trigger Type</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Detected</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{getTriggerBadge(row.trigger_type)}</TableCell>
                  <TableCell className="max-w-md">{row.reason || '—'}</TableCell>
                  <TableCell>{getStatusBadge(row.processed)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(row.created_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
